import { useCallback } from 'react';
import { Container, Row, Col, Typo } from '@geberit/gdds';

// styles
import styles from './text-image.module.scss';
import { textStyle } from './styles';

// types
import type { HeadlineComponentProps, MediaProps, TextImageProps } from '.';

// components
import { InnerHtml } from 'components/InnerHtml/inner-html';
import { CmsLink } from 'components/Link/CmsLink';
import { SectionClosingLine } from '../Common/section-closing-line';
import { Image } from 'components/image/image';
import ImageVideo from './Views/image-video';
import { Headline } from './headline';

// utils
import { imageStyles, aboveHeadline, getComponentsByPosition, checkHasImage, getPosition } from '.';
import { classNameBuilder } from 'utils/classNameBuilder';
import { decodingContent } from 'utils/decodingContent';
import { buildSize, gridSizes } from 'utils/gridSize';
import { useThemeName } from 'utils/hooks/use-theme';
import { useSectionId } from 'utils/hooks/use-section-id';
import { useGroup } from 'utils/hooks/use-group';
import { useXy } from 'utils/hooks/use-xy';
import { Formats } from '../headline/headlines.types';
import { isVideoItem } from 'components/ContentElementsGdds/TextImage/utils';

export function TextImage(props: Readonly<TextImageProps>) {
  const {
    pictureObject,
    pictureAlt,
    content,
    title,
    subtitle,
    subline,
    emphasize,
    caption,
    contentIndex,
    imagePosition,
    childOf,
    spacing,
    border,
    images,
    mediaItems,
    mediaPosition,
    anchor,
    titleInAnchor,
    link,
    columns,
    isxMark,
    isChild = false,
  } = props;

  const theme = useThemeName();
  const isGroup = useGroup();
  const isXy = useXy();
  const position = getPosition(mediaPosition, imagePosition, columns);
  const sectionId = useSectionId(title, anchor, titleInAnchor, contentIndex);
  const hasImage = checkHasImage(pictureObject, images, mediaItems, !isGroup);
  const isCheckmarkRenderActive = Boolean(isxMark);

  const TextImageComponent =
    getComponentsByPosition({ theme, mediaPosition: position }) || (() => null);

  // Should be always white, backgroundColor from props is not used.
  const whiteBackgroundColor = 'white';

  const showHeadline =
    aboveHeadline[theme] && !mediaPosition?.includes('full') && !mediaPosition?.includes('in_row');

  const hasVideos = (mediaItems as MediaProps[])?.some((item) => isVideoItem(item));
  const hasImages = (mediaItems as MediaProps[])?.some((item) => !isVideoItem(item));

  const HeadlineComponent = useCallback(
    ({ format, aboveHeadline, className }: Readonly<HeadlineComponentProps>) => (
      <Headline
        title={title}
        subtitle={subtitle}
        subline={subline}
        emphasize={emphasize}
        format={format}
        aboveHeadline={aboveHeadline}
        className={className}
        childOf={childOf}
        isChild={isChild}
        sublineNoHTag={isXy}
      />
    ),
    [title, subtitle, subline, emphasize, isChild, childOf],
  );

  const ImageComponent = useCallback(
    ({ onlyImages = false, onlyVideos = false }) =>
      isGroup ? (
        <Image data={pictureObject} alt={pictureAlt} title={pictureAlt} />
      ) : (
        <ImageVideo
          media={mediaItems as MediaProps[]}
          onlyImages={onlyImages}
          onlyVideos={onlyVideos}
        />
      ),
    [pictureObject, pictureAlt, mediaItems, isGroup],
  );

  const CaptionComponent = useCallback(
    () => (
      <Typo tag="div" size={textStyle.caption} className={styles.caption}>
        {decodingContent(caption)}
      </Typo>
    ),
    [caption],
  );

  const ContentComponent = useCallback(
    () => (
      <Typo tag="div" fontWeight={400} size={textStyle.paragraph}>
        <InnerHtml
          as="div"
          previewId={isGroup ? '#st_content' : '#st_copytext'}
          content={content}
          className={classNameBuilder(styles.content, !hasImage && styles.noImage)}
          gddsHeadlineTag={isChild && isXy ? Formats.h5 : Formats.h3}
          isGdds
          headlineWrapperNoMarginTop
          isCheckmarkRenderActive={isCheckmarkRenderActive}
        />
      </Typo>
    ),
    [content, hasImage, isGroup],
  );

  const LinkComponent = useCallback(
    () => (
      <CmsLink
        link={link as Link}
        stylingType="primary"
        standardFontSize={isGroup}
        alignByContent={isGroup ? false : 'left'}
      />
    ),
    [isGroup, link],
  );

  return (
    <section
      className={classNameBuilder(
        styles.wrapper,
        imageStyles[position],
        styles[`${whiteBackgroundColor}Background`],
        childOf === 'accordion' && styles.marginIsAccordion,
        childOf === 'tab' && styles.marginIsTab,
        spacing?.top === 'small' && styles.marginTopSmall,
        spacing?.bottom === 'small' && styles.marginBottomSmall,
      )}
      id={sectionId}
    >
      {showHeadline && (
        <Container maxContentWidth={buildSize(gridSizes.gddsFullGrid)}>
          <Row>
            <Col>
              <HeadlineComponent
                className={styles.withMarginBottom}
                aboveHeadline={aboveHeadline[theme]}
              />
            </Col>
          </Row>
        </Container>
      )}
      <TextImageComponent
        headline={title || subtitle || subline ? HeadlineComponent : undefined}
        image={hasImage ? ImageComponent : undefined}
        caption={caption ? CaptionComponent : undefined}
        content={content ? ContentComponent : undefined}
        imagePosition={position}
        theme={theme}
        link={link ? LinkComponent : undefined}
        mediaPosition={mediaPosition}
        hasVideos={hasVideos}
        hasImages={hasImages}
      />
      {((!border && childOf !== 'accordion') || (!isGroup && !border)) && (
        <SectionClosingLine
          withChildAccordionMargin={childOf === 'accordion'}
          withChildTabMargin={childOf === 'tab'}
        />
      )}
    </section>
  );
}
